import { Modal, styled, Typography, useTheme } from "@mui/material";
import { useCallback } from "react";
import HTMLRichText from "./HTMLRichText";
import { ThemeButton } from "./ThemeComponents/ThemeButton";
import {
  CONSENT_CONFIRM_LABEL,
  CONSENT_DESCRIPTION,
  CONSENT_HEADER,
  CONSENT_REJECT_LABEL,
  CONSENT_REJECT_URL,
} from "../../../theme/defaultConstants";
import { iframe } from "../../helpers/environment";
import { useAnalyticsTracker } from "../../app/AnalyticsTracker";

interface Props {
  onConfirm: () => void;
}

const ModalContainer = styled("div")(({ theme }) => ({
  background: theme.palette.background.default,
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius,
  fontSize: theme.typography.caption.fontSize,
  maxWidth: 500,
  width: "90%",
  maxHeight: "90vh",
  overflowY: "auto",
  position: "absolute",
  top: iframe ? 40 : "50%",
  transform: iframe ? "translateX(-50%)" : "translate(-50%, -50%)",
  left: "50%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  gap: theme.spacing(3),
  outline: 0,
}));

const ButtonRow = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  gap: theme.spacing(2),
}));

const StyledModal = styled(Modal)(() => ({
  zIndex: 9999999999,
}));

const StyledThemeButton = styled(ThemeButton)(() => ({
  flex: 1,
}));

const Header = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.h4.fontSize,
  color: theme.palette.primary.main,
  textAlign: "center",
}));

function ConsentModal({ onConfirm }: Props) {
  const { settings } = useTheme();
  const { AnalyticsTracker } = useAnalyticsTracker();

  const handleConfirm = useCallback(() => {
    AnalyticsTracker.trackIfConsentConfirm();
    onConfirm();
  }, [AnalyticsTracker, onConfirm]);

  const {
    header = CONSENT_HEADER,
    confirmButtonLabel = CONSENT_CONFIRM_LABEL,
    description = CONSENT_DESCRIPTION,
    rejectButtonLabel = CONSENT_REJECT_LABEL,
    rejectRedirectUrl = CONSENT_REJECT_URL,
  } = settings?.consentModal || {};

  return (
    <StyledModal open role="dialog" aria-modal="true">
      <ModalContainer>
        {header && <Header variant="h2">{header}</Header>}
        {description && <HTMLRichText html={description} />}
        <ButtonRow>
          {rejectButtonLabel && rejectRedirectUrl && (
            <StyledThemeButton
              href={rejectRedirectUrl}
              customVariant="secondary"
            >
              {rejectButtonLabel}
            </StyledThemeButton>
          )}
          {confirmButtonLabel && (
            <StyledThemeButton onClick={handleConfirm}>
              {confirmButtonLabel}
            </StyledThemeButton>
          )}
        </ButtonRow>
      </ModalContainer>
    </StyledModal>
  );
}

export default ConsentModal;
